import isNumber from 'lodash-es/isNumber';
import {
    ACTION_TO_WATCHDOG_SUBSCRIBE,
    BAZAAR_WATCHDOG_CANCELED,
    BAZAAR_WATCHDOG_SAVED,
    PAGE_SLUG,
} from 'mk/autogenerated/translations/BazaarGrid.sagas.02a97a78b6ab52e5cbf09312df5d241e'
import {
    bazaar_api_v3_products_url,
    bazaar_api_v3_watchdog_subscribe_url,
    bazaar_api_v3_watchdog_unsubscribe_url,
} from 'mk/urls/functions';
import { LOGIN } from 'mk2/actions';
import { FacetsData } from 'mk2/apps/bazaar/components/FacetsFilter';
import { ITEMS_PER_PAGE } from 'mk2/apps/bazaar/constants';
import {
    bazaarGridLoadApi,
    bazaarGridWatchdogSubscribeApi,
    bazaarGridWatchdogUnsubscribeApi,
    BazaarGridLoadAction,
    BazaarGridLoadNormalizedResponse,
    BazaarGridWatchdogSubscribeAction,
    BazaarGridWatchdogUnsubscribeAction,
    BAZAAR_GRID_LOAD,
    BAZAAR_GRID_WATCHDOG_SUBSCRIBE,
    BAZAAR_GRID_WATCHDOG_UNSUBSCRIBE,
    Checked,
} from 'mk2/apps/bazaar/containers/BazaarGrid/BazaarGrid.actions';
import { FilterType } from 'mk2/apps/bazaar/schemas';
import {
    handleXHRGetErrorSaga,
    handleXHRPostErrorSaga,
    normalizeError,
    XHRAction,
    XHRGetError,
    XHRPostError,
} from 'mk2/helpers/api';
import { showSuccessToast } from 'mk2/helpers/toasts';
import { getLogger } from 'mk2/logger';
import { checkIfUserIsLoggedIn } from 'mk2/sagas/auth';
import { getRoutingLocation } from 'mk2/selectors';
import { redirectInPWASaga } from 'mk2/services/browserHistory';
import React from 'react';
import { all, call, put, select, take, takeEvery } from 'redux-saga/effects';

const logger = getLogger('bazaar/BazaarGrid.sagas');

interface LoadApiResponse {
    body: {
        facets: FacetsData;
        hits: number;
        hitsAll: number;
        hitsLiked: number;
        checked: Checked;
        watchdog: boolean;
        strollersIdsOrdered: number[];
        entities: BazaarGridLoadNormalizedResponse['entities'];
        result: BazaarGridLoadNormalizedResponse['result'];
    };
}

function* load({ xhr, filter, watchdogKey }: BazaarGridLoadAction & XHRAction) {
    yield put(bazaarGridLoadApi.request(filter, watchdogKey));

    const { page, start, ...filterRest } = filter;

    try {
        const response: LoadApiResponse = yield call(() =>
            xhr.post(bazaar_api_v3_products_url(), {
                filter,
                filterType: FilterType.BAZAAR,
                watchdogKey,
            }),
        );

        yield put(
            bazaarGridLoadApi.success(
                filter,
                watchdogKey,
                response.body.facets,
                response.body.hits,
                response.body.hitsAll,
                response.body.hitsLiked,
                response.body.checked,
                response.body.watchdog,
                response.body.strollersIdsOrdered,
                {
                    entities: response.body.entities,
                    result: response.body.result,
                },
            ),
        );
    } catch (error) {
        if (error.status === 404 && error.response && error.response.body.page && page !== error.response.body.page) {
            const newPage = error.response.body.page;
            const location = yield select(getRoutingLocation);
            const destination = `${location.pathname}${location.search}${location.hash}`;
            yield redirectInPWASaga(
                destination.replace(new RegExp(`${PAGE_SLUG}-\\d+/`), newPage === 1 ? '' : `${PAGE_SLUG}-${newPage}/`),
                302,
            );
        } else {
            yield handleXHRGetErrorSaga(error as XHRGetError, logger);
            yield put(bazaarGridLoadApi.failure(filter, watchdogKey, normalizeError(error)));
        }
    }
}

interface WatchdogSubscribeApi {
    body: {};
}

function* watchdogSubscribe({ xhr, watchdogKey, filter }: BazaarGridWatchdogSubscribeAction & XHRAction) {
    if (!(yield call(checkIfUserIsLoggedIn, ACTION_TO_WATCHDOG_SUBSCRIBE))) {
        // If not logged in - wait for LOGIN
        yield take(LOGIN);
    }

    yield put(bazaarGridWatchdogSubscribeApi.request(filter, watchdogKey));

    try {
        const response: WatchdogSubscribeApi = yield call(() =>
            xhr.post(bazaar_api_v3_watchdog_subscribe_url(), {
                watchdogKey,
                filter: {
                    ...filter,
                    settings: {
                        ...filter.settings,
                        cleanSort: true,
                        gridSort: 'watchdog', // Sort by watchdog - custom order
                    },
                    page: undefined, // Reset page
                },
                filterType: FilterType.BAZAAR,
            }),
        );

        const normalizedResponse = {
            entities: {},
            result: {},
        };

        yield put(bazaarGridWatchdogSubscribeApi.success(filter, watchdogKey, normalizedResponse));
        yield showSuccessToast(BAZAAR_WATCHDOG_SAVED);
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, logger);
        yield put(bazaarGridWatchdogSubscribeApi.failure(filter, watchdogKey, normalizeError(error)));
    }
}

interface WatchdogUnsubscribeApi {
    body: {};
}

function* watchdogUnsubscribe({ xhr, watchdogKey, filter }: BazaarGridWatchdogUnsubscribeAction & XHRAction) {
    if (!(yield call(checkIfUserIsLoggedIn, ACTION_TO_WATCHDOG_SUBSCRIBE))) {
        return;
    }

    yield put(bazaarGridWatchdogUnsubscribeApi.request(filter, watchdogKey));

    try {
        const response: WatchdogUnsubscribeApi = yield call(() =>
            xhr.post(bazaar_api_v3_watchdog_unsubscribe_url(), {
                watchdogKey,
            }),
        );

        const normalizedResponse = {
            entities: {},
            result: {},
        };

        yield put(bazaarGridWatchdogUnsubscribeApi.success(filter, watchdogKey, normalizedResponse));
        yield showSuccessToast(BAZAAR_WATCHDOG_CANCELED);
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, logger);
        yield put(bazaarGridWatchdogUnsubscribeApi.failure(filter, watchdogKey, normalizeError(error)));
    }
}

export default function* root() {
    yield all([
        takeEvery(BAZAAR_GRID_LOAD, load),
        takeEvery(BAZAAR_GRID_WATCHDOG_SUBSCRIBE, watchdogSubscribe),
        takeEvery(BAZAAR_GRID_WATCHDOG_UNSUBSCRIBE, watchdogUnsubscribe),
    ]);
}
